import Vue from 'vue';
import router from './router';
import { VueExtendLayout, layout } from 'vue-extend-layout';
import Vuetify from 'vuetify';
import 'vuetify/dist/vuetify.min.css';
import 'material-icons/iconfont/material-icons.css';
import 'typeface-roboto';
import store from './store';
import { client } from './services';
import Vue2Filters from 'vue2-filters';
import vueg from 'vueg';
import VueProgressBar from 'vue-progressbar';
import axios from 'axios';
import Snotify, { SnotifyPosition } from 'vue-snotify';
import 'vue-snotify/styles/material.css';
import config from './config';
import { VENUE_STORAGE } from '@/constants';
import './styles/global.css';

axios.interceptors.request.use(
  config => {
    const token = localStorage.getItem('feathers-jwt');
    if (
      token &&
      !config.url.endsWith('/authentication') &&
      !config.url.includes('/verify-jwt') &&
      !config.url.includes('/socket.io/')
    ) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    const method = (config.method || 'get').toUpperCase();
    const unsafe = method !== 'GET' && method !== 'HEAD' && method !== 'OPTIONS';
    if (unsafe && !config.headers['X-CSRF-Token']) {
      const match = document.cookie.split('; ').find(row => row.startsWith('XSRF-TOKEN='));
      if (match) {
        const csrfToken = decodeURIComponent(match.split('=')[1]);
        config.headers['X-CSRF-Token'] = csrfToken;
      }
    }

    return config;
  },
  error => Promise.reject(error)
);

axios.interceptors.response.use(
  response => response,
  error => {
    if (error.response && error.response.status === 401) {
      const errorMessage = (error.response.data && error.response.data.message) || '';
      const errorCode = (error.response.data && error.response.data.code) || '';

      const isSessionInvalidated =
        errorMessage.includes('Session invalidated') ||
        errorMessage.includes('password change') ||
        errorCode === 'TOKEN_BLACKLISTED';

      if (isSessionInvalidated) {
        console.log('Axios: Session invalidated - redirecting to login');

        localStorage.removeItem('feathers-jwt');
        localStorage.removeItem('SELECTED_CMS_VENUE');
        document.cookie = 'feathers-jwt=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/';

        window.location.replace('/');
      }
    }
    return Promise.reject(error);
  }
);

Vue.config.productionTip = false;
Vue.use(VueExtendLayout);

const selected = JSON.parse(localStorage.getItem(VENUE_STORAGE));
if (selected && selected.primaryColor && selected.secondaryColor) {
  setBannerColor(selected.primaryColor, selected.secondaryColor);
} else {
  setBannerColor();
}

Vue.use(Vue2Filters);
Vue.use(vueg, router, {
  shadow: false,
  forwardAnim: 'fadeIn',
  backAnim: 'fadeIn'
});
Vue.use(VueProgressBar);
Vue.use(Snotify, {
  toast: {
    position: SnotifyPosition.rightTop,
    timeout: 2000
  }
});
Vue.prototype.$axios = axios;
Vue.prototype.$ROOT_URL = config.server_url;

async function fetchCsrfToken() {
  try {
    const { data } = await axios.get(`${config.server_url}/csrf-token`, { withCredentials: true });
    if (data && data.csrfToken) {
      axios.defaults.headers.common['X-CSRF-Token'] = data.csrfToken;
    }
  } catch (e) {
    console.warn('Unable to fetch CSRF token at init:', e && e.message);
  }
}
fetchCsrfToken();

axios
  .get(`${config.server_url}/get_api_url_config`)
  .then(response => {
    const config = response.data;
    Vue.prototype.$API_URL = config.api_url;
    console.log(config.api_url);
  })
  .catch(error => {
    console.error('Error fetching configuration:', error);
  });

const VBtn = Vue.component('VBtn');
VBtn.options.props.ripple.default = false;

let app;
let sessionListenerSetup = false;

function setBannerColor(
  primaryColor = config.theme.peterBlue,
  secondaryColor = config.theme.sidebarColor
) {
  Vue.use(Vuetify, {
    theme: { ...config.theme, peterBlue: primaryColor, sidebarColor: secondaryColor, fontsize: 14 }
  });
}

function setupSessionInvalidationListener(user) {
  if (!user || !user._id) {
    console.warn('setupSessionInvalidationListener: No user or user._id');
    return;
  }

  if (sessionListenerSetup) {
    console.log('Session listener already setup, skipping...');
    return;
  }

  const currentUserId = user._id;
  console.log('Setting up session invalidation listener for user:', currentUserId);
  console.log('Socket connected:', client.io && client.io.connected);

  client.io.off('user-session-invalidated');

  client.io.on('user-session-invalidated', data => {
    console.log('Received user-session-invalidated event');
    console.log('Event userId:', data.userId, '| Current userId:', currentUserId);

    if (data.userId === currentUserId || String(data.userId) === String(currentUserId)) {
      console.log('Current user session invalidated - initiating logout');

      localStorage.removeItem('feathers-jwt');
      localStorage.removeItem('SELECTED_CMS_VENUE');
      document.cookie = 'feathers-jwt=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/';

      store.commit('users/SET_USER', null);
      store.commit('users/SET_AUTH_STATE', false);

      let message = 'Your session has expired. Please login again.';
      if (data.reason === 'password_change') {
        message = 'Your password was changed. Please login again.';
      }

      if (window.app && window.app.$snotify) {
        window.app.$snotify.warning(message, 'Session Expired', {
          timeout: 3000,
          showProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true
        });
      }

      setTimeout(() => {
        window.location.replace('/');
      }, 1000);
    } else {
      console.log('Event for different user - ignoring');
    }
  });

  client.io.on('connect', () => console.log('Socket.IO connected'));
  client.io.on('disconnect', () => console.log('Socket.IO disconnected'));
  client.io.on('error', error => console.error('Socket.IO error:', error));

  sessionListenerSetup = true;
  console.log('Session invalidation listener setup complete');
}

function setupCookieSync() {
  function syncCookie() {
    const jwtFromStorage = localStorage.getItem('feathers-jwt');
    const jwtFromCookie = getCookie('feathers-jwt');

    if (jwtFromStorage && !jwtFromCookie) {
      console.warn('[Auto-sync] Cookie missing, restoring from localStorage');
      const isSecure = window.location.protocol === 'https:';
      const maxAge = 2 * 60 * 60;
      const secureSuffix = isSecure ? '; Secure' : '';
      document.cookie = `feathers-jwt=${jwtFromStorage}; path=/; max-age=${maxAge}; SameSite=Lax${secureSuffix}`;
    }
  }

  function getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2)
      return parts
        .pop()
        .split(';')
        .shift();
  }

  syncCookie();
  setInterval(syncCookie, 60000);
}

async function init() {
  if (app) {
    return;
  }

  try {
    const response = await axios.get(config.server_url + '/api/settings/cms-version');
    if (response.data.success) {
      store.commit('SET_VERSION', {
        version: response.data.version,
        isFullVersion: response.data.isFullVersion
      });
    } else {
      console.log('Error:', response.data.message);
    }
  } catch (error) {
    console.log(`Error getting cms version - ${error}`);
  }

  // try {
  //   await store.dispatch('license/getLicense');

  //   if (!store.state.license.hasError) {
  //     try {
  //       const data = await axios.get(config.server_url + '/settings');
  //       if (data.data.settings_tab) {
  //         store.commit('SET_SETTINGS_TAB', data.data.settings_tab);
  //       }

  //       if (data.data.assets) {
  //         store.commit('assets/SET_SETTINGS_ASSETS', data.data.assets);
  //       }
  //     } catch (error) {
  //       console.log(`Error getting settings - ${error}`);
  //     }
  //   }
  // } catch (error) {
  //   console.log(`Error getting license - ${error}`);
  // }

  try {
    let user;
    const jwtExistInLocalStorage = !!localStorage.getItem('feathers-jwt');
    const usersService = client.service('users');

    if (jwtExistInLocalStorage) {
      const jwt = localStorage.getItem('feathers-jwt');

      const isSecure = window.location.protocol === 'https:';
      const maxAge = 2 * 60 * 60;
      const secureSuffix = isSecure ? '; Secure' : '';

      document.cookie = `feathers-jwt=${jwt}; path=/; max-age=${maxAge}; SameSite=Lax${secureSuffix}`;

      const response = await client.authenticate();
      const payload = await client.passport.verifyJWT(response.accessToken);
      user = await usersService.get(payload.userId);
    }

    if (!user) {
      throw new Error('Not authenticated');
    }
    store.commit('users/SET_USER', user);

    if (user.venues && user.venues.length > 0) {
      await store.dispatch('users/GET_USER_VENUES', user.venues || []);
    }
    store.commit('users/SET_AUTH_STATE', true);

    app = new Vue({
      el: '#app',
      store,
      router,
      ...layout
    });

    window.app = app;
    setupSessionInvalidationListener(user);
    setupCookieSync();
  } catch (e) {
    store.commit('users/SET_AUTH_STATE', false);
    app = new Vue({
      el: '#app',
      store,
      router,
      ...layout
    });
  }
}

init();



// WEBPACK FOOTER //
// ./src/main.js